import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    Tooltip,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from '@mui/material';
import { Add, Delete, Edit, FilterList, Clear, ExpandMore, ExpandLess } from '@mui/icons-material';
import { MainLayout } from '../components/MainLayout';
import { httpClient } from '../services/http-client';
import { snapshotsService } from '../services/snapshots-service';
import { accountsService } from '../services/accounts-service';
import { SnapshotWithAccount, Account } from '../types/api';

export const SnapshotsPage: React.FC = () => {
    const [snapshots, setSnapshots] = useState<SnapshotWithAccount[]>([]);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalRows, setTotalRows] = useState(0);

    // Editing
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingSnapshot, setEditingSnapshot] = useState<SnapshotWithAccount | null>(null);
    const [editForm, setEditForm] = useState({
        date: '',
        balance: '',
        description: '',
    });
    const [saving, setSaving] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingSnapshot, setDeletingSnapshot] = useState<SnapshotWithAccount | null>(null);
    const [deleting, setDeleting] = useState(false);

    // Adding
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [addForm, setAddForm] = useState({
        account_id: '',
        date: '',
        balance: '',
        description: '',
    });
    const [adding, setAdding] = useState(false);

    // Filtering
    const [filters, setFilters] = useState({
        account_id: '',
        account_type: '',
        start_date: '',
        end_date: '',
        month: '',
        year: '',
        balance_min: '',
        balance_max: '',
        description: '',
    });
    const [extraFiltersExpanded, setExtraFiltersExpanded] = useState(false);

    // Sorting
    type SortField = 'date' | 'account_name' | 'account_type' | 'balance' | 'description';
    type SortDirection = 'asc' | 'desc';
    const [sortField, setSortField] = useState<SortField>('date');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const fetchSnapshots = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Check if we have client-side filters active
            const hasClientSideFilters = !!(filters.account_type || filters.balance_min || filters.balance_max || filters.description);
            
            // Build server-side filters
            // If client-side filters are active, fetch more data to filter client-side
            const serverFilters: any = {
                page: hasClientSideFilters ? 1 : page + 1,
                per_page: hasClientSideFilters ? 1000 : rowsPerPage, // Fetch more when client-side filtering
            };
            
            if (filters.account_id) {
                serverFilters.account_id = filters.account_id;
            }
            // Handle month/year filters - convert to start_date/end_date if set
            let startDate = filters.start_date;
            let endDate = filters.end_date;
            
            if (filters.month && filters.year) {
                // Set to first and last day of the selected month
                const month = parseInt(filters.month);
                const year = parseInt(filters.year);
                const firstDay = new Date(year, month - 1, 1);
                const lastDay = new Date(year, month, 0);
                startDate = firstDay.toISOString().split('T')[0];
                endDate = lastDay.toISOString().split('T')[0];
            }
            
            if (startDate) {
                serverFilters.start_date = startDate;
            }
            if (endDate) {
                serverFilters.end_date = endDate;
            }
            
            const response = await snapshotsService.getSnapshotsWithAccounts(serverFilters);

            // Service now properly wraps response as ApiResponse<PaginatedResponse<T>>
            const manualData = response?.data?.data || [];

            // Merge in month-end balances DERIVED FROM THE LEDGER.
            //
            // The snapshots table is written only by hand, and every importer
            // posts journal entries instead -- so for any household whose data
            // arrived through Plaid or a CSV, which is all of them, this page
            // was permanently empty while the ledger held hundreds of
            // positions. Manual entry still works and is still editable; a
            // derived row is arithmetic and is marked read-only, because
            // "correcting" one would mean editing a sum rather than data.
            let derived: any[] = [];
            try {
                const hist = await httpClient.get<any>('/snapshots/history?months=24');
                const points = ((hist as any)?.data?.points ?? (hist as any)?.points ?? []) as any[];
                derived = points
                    .filter((pt) => pt.source === 'ledger')
                    .map((pt, i) => ({
                        id: `ledger-${pt.account_id}-${pt.date}-${i}`,
                        account_id: pt.account_id,
                        date: pt.date,
                        balance: String(pt.balance),
                        amount: String(pt.balance),
                        description: 'Month-end balance from the ledger',
                        notes: '',
                        source: 'ledger',
                        derived: true,
                        account_name: pt.account,
                        account_type: pt.account_type,
                        account_currency: pt.currency,
                        created_at: pt.date,
                        updated_at: pt.date,
                    }));
            } catch {
                // Manual snapshots still render; a derived-history failure is
                // not a reason to show nothing.
            }

            const snapshotsData = [...manualData, ...derived];
            
            // Map to SnapshotWithAccount format (backend includes account_name but not account_type/account_currency)
            // Merge with account data if available to get account_type and account_currency
            const accountMap = new Map((accounts || []).map(acc => [acc.id, acc]));
            let mappedSnapshots = (snapshotsData || []).map((snapshot: any) => {
                const account = accountMap.get(snapshot.account_id);
                return {
                    ...snapshot,
                    account_type: snapshot.account_type || account?.account_type || 'Asset',
                    account_currency: snapshot.account_currency || account?.currency || 'CAD',
                };
            });
            
            // Apply client-side filters
            mappedSnapshots = mappedSnapshots.filter((snapshot: SnapshotWithAccount) => {
                // Filter by account type
                if (filters.account_type && snapshot.account_type !== filters.account_type) {
                    return false;
                }
                
                // Filter by balance min
                if (filters.balance_min) {
                    const minBalance = parseFloat(filters.balance_min);
                    const snapshotBalance = parseFloat(snapshot.balance || '0');
                    if (snapshotBalance < minBalance) {
                        return false;
                    }
                }
                
                // Filter by balance max
                if (filters.balance_max) {
                    const maxBalance = parseFloat(filters.balance_max);
                    const snapshotBalance = parseFloat(snapshot.balance || '0');
                    if (snapshotBalance > maxBalance) {
                        return false;
                    }
                }
                
                // Filter by description (case-insensitive search)
                if (filters.description) {
                    const searchTerm = filters.description.toLowerCase();
                    const snapshotDescription = (snapshot.description || '').toLowerCase();
                    if (!snapshotDescription.includes(searchTerm)) {
                        return false;
                    }
                }
                
                return true;
            });
            
            // Apply sorting
            mappedSnapshots.sort((a: SnapshotWithAccount, b: SnapshotWithAccount) => {
                let aValue: any;
                let bValue: any;
                
                switch (sortField) {
                    case 'date':
                        aValue = new Date(a.date).getTime();
                        bValue = new Date(b.date).getTime();
                        break;
                    case 'account_name':
                        aValue = (a.account_name || '').toLowerCase();
                        bValue = (b.account_name || '').toLowerCase();
                        break;
                    case 'account_type':
                        aValue = (a.account_type || '').toLowerCase();
                        bValue = (b.account_type || '').toLowerCase();
                        break;
                    case 'balance':
                        aValue = parseFloat(a.balance || '0');
                        bValue = parseFloat(b.balance || '0');
                        break;
                    case 'description':
                        aValue = (a.description || '').toLowerCase();
                        bValue = (b.description || '').toLowerCase();
                        break;
                    default:
                        return 0;
                }
                
                if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
                return 0;
            });
            
            // Apply client-side pagination if we fetched all data (client-side filters active)
            let paginatedSnapshots = mappedSnapshots;
            
            if (hasClientSideFilters) {
                // Apply client-side pagination
                const startIndex = page * rowsPerPage;
                const endIndex = startIndex + rowsPerPage;
                paginatedSnapshots = mappedSnapshots.slice(startIndex, endIndex);
            }
            
            setSnapshots(paginatedSnapshots);
            setTotalRows(mappedSnapshots.length); // Total after all filters applied
        } catch (err: any) {
            const errorMessage = err?.message || err?.error || 'Failed to fetch snapshots';
            setError(errorMessage);
            console.error('Error fetching snapshots:', err);
            setSnapshots([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, accounts, filters]);

    const fetchAccounts = async () => {
        try {
            const response = await accountsService.getAccounts();
            
            // Service now properly wraps response as ApiResponse<PaginatedResponse<T>>
            const accountsData = response?.data?.data || [];
            setAccounts(Array.isArray(accountsData) ? accountsData : []);
        } catch (err: any) {
            console.error('Failed to fetch accounts:', err);
            setAccounts([]);
        }
    };

    useEffect(() => {
        fetchAccounts();
    }, []);

    useEffect(() => {
        fetchSnapshots();
    }, [fetchSnapshots]);

    // Reset page when filters change
    useEffect(() => {
        setPage(0);
    }, [filters.account_id, filters.account_type, filters.start_date, filters.end_date, filters.month, filters.year, filters.balance_min, filters.balance_max, filters.description]);

    const handleFilterChange = (key: string, value: string) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleClearFilters = () => {
        setFilters({
            account_id: '',
            account_type: '',
            start_date: '',
            end_date: '',
            month: '',
            year: '',
            balance_min: '',
            balance_max: '',
            description: '',
        });
        setPage(0);
    };

    // Get current year and generate last 5 years for year filter
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
    const months = [
        { value: '1', label: 'January' },
        { value: '2', label: 'February' },
        { value: '3', label: 'March' },
        { value: '4', label: 'April' },
        { value: '5', label: 'May' },
        { value: '6', label: 'June' },
        { value: '7', label: 'July' },
        { value: '8', label: 'August' },
        { value: '9', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' },
    ];

    const hasActiveFilters = Object.values(filters).some((value) => value !== '');
    const hasExtraFilters = !!(filters.month || filters.year || filters.balance_min || filters.balance_max);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleEditClick = (snapshot: SnapshotWithAccount) => {
        setEditingSnapshot(snapshot);
        setEditForm({
            date: snapshot.date.split('T')[0],
            balance: snapshot.balance,
            description: snapshot.description || '',
        });
        setEditDialogOpen(true);
    };

    const handleSave = async () => {
        if (!editingSnapshot) return;
        setSaving(true);
        try {
            await snapshotsService.updateSnapshot(editingSnapshot.id, {
                balance: editForm.balance,
                description: editForm.description,
            });
            setEditDialogOpen(false);
            setSuccessMessage('Snapshot updated successfully.');
            setError(null);
            fetchSnapshots();
        } catch (err: any) {
            setError(err.message || 'Failed to update snapshot');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteClick = (snapshot: SnapshotWithAccount) => {
        setDeletingSnapshot(snapshot);
        setDeleteDialogOpen(true);
    };

    const handleDelete = async () => {
        if (!deletingSnapshot) return;
        setDeleting(true);
        try {
            await snapshotsService.deleteSnapshot(deletingSnapshot.id);
            setDeleteDialogOpen(false);
            setDeletingSnapshot(null);
            setSuccessMessage('Snapshot deleted successfully.');
            setError(null);
            fetchSnapshots();
        } catch (err: any) {
            setError(err.message || 'Failed to delete snapshot');
        } finally {
            setDeleting(false);
        }
    };

    const defaultAddDate = useMemo(() => new Date().toISOString().split('T')[0], []);

    const handleOpenAddDialog = () => {
        setAddForm({
            account_id: accounts[0]?.id || '',
            date: defaultAddDate,
            balance: '',
            description: '',
        });
        setAddDialogOpen(true);
    };

    const handleAddSnapshot = async () => {
        if (!addForm.account_id || !addForm.date || addForm.balance === '') {
            setError('Please select an account, date, and balance for the snapshot.');
            return;
        }
        setAdding(true);
        try {
            await snapshotsService.createSnapshot({
                account_id: addForm.account_id,
                date: addForm.date,
                balance: addForm.balance,
                description: addForm.description || undefined,
            });
            setAddDialogOpen(false);
            setSuccessMessage('Snapshot added successfully.');
            setError(null);
            fetchSnapshots();
        } catch (err: any) {
            setError(err.message || 'Failed to add snapshot');
        } finally {
            setAdding(false);
        }
    };

    const closeAddDialog = () => setAddDialogOpen(false);
    const closeEditDialog = () => setEditDialogOpen(false);
    const closeDeleteDialog = () => setDeleteDialogOpen(false);

    const formatCurrency = (amount: string, currency: string) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
        }).format(parseFloat(amount));
    };

    return (
        <MainLayout>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                        All Snapshots
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        View and manage history for all your accounts
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<Add />} onClick={handleOpenAddDialog} disabled={accounts.length === 0}>
                    Add Snapshot
                </Button>
            </Box>

            {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
                    {successMessage}
                </Alert>
            )}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Filters Section */}
            <Paper sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider' }}>
                {/* Visible Filters: Account, Account Type, Date, Description */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
                    {/* Account Filter */}
                    <FormControl fullWidth size="small">
                        <InputLabel>Account</InputLabel>
                        <Select
                            value={filters.account_id}
                            label="Account"
                            onChange={(e) => handleFilterChange('account_id', e.target.value)}
                        >
                            <MenuItem value="">All Accounts</MenuItem>
                            {accounts.map((account) => (
                                <MenuItem key={account.id} value={account.id}>
                                    {account.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Account Type Filter */}
                    <FormControl fullWidth size="small">
                        <InputLabel>Account Type</InputLabel>
                        <Select
                            value={filters.account_type}
                            label="Account Type"
                            onChange={(e) => handleFilterChange('account_type', e.target.value)}
                        >
                            <MenuItem value="">All Types</MenuItem>
                            <MenuItem value="Asset">Asset</MenuItem>
                            <MenuItem value="Debt">Debt</MenuItem>
                            <MenuItem value="Investment">Investment</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Start Date Filter */}
                    <TextField
                        label="Start Date"
                        type="date"
                        size="small"
                        value={filters.start_date}
                        onChange={(e) => {
                            handleFilterChange('start_date', e.target.value);
                            if (e.target.value) {
                                handleFilterChange('month', '');
                                handleFilterChange('year', '');
                            }
                        }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />

                    {/* End Date Filter */}
                    <TextField
                        label="End Date"
                        type="date"
                        size="small"
                        value={filters.end_date}
                        onChange={(e) => {
                            handleFilterChange('end_date', e.target.value);
                            if (e.target.value) {
                                handleFilterChange('month', '');
                                handleFilterChange('year', '');
                            }
                        }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                </Box>

                {/* Description Filter - Full Width */}
                <Box sx={{ mb: 2 }}>
                    <TextField
                        label="Search Description"
                        type="text"
                        size="small"
                        value={filters.description}
                        onChange={(e) => handleFilterChange('description', e.target.value)}
                        placeholder="Enter keywords..."
                        fullWidth
                    />
                </Box>

                {/* Extra Filters - Collapsible */}
                <Box>
                    <Button
                        startIcon={extraFiltersExpanded ? <ExpandLess /> : <ExpandMore />}
                        onClick={() => setExtraFiltersExpanded(!extraFiltersExpanded)}
                        variant={hasExtraFilters ? 'contained' : 'outlined'}
                        size="small"
                        sx={{ mb: extraFiltersExpanded ? 2 : 0 }}
                    >
                        {hasExtraFilters ? 'Extra Filters' : 'Show Extra Filters'} {hasExtraFilters && `(${[filters.month, filters.year, filters.balance_min, filters.balance_max].filter(v => v !== '').length})`}
                    </Button>

                    <Collapse in={extraFiltersExpanded}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, mt: 2 }}>
                            {/* Month Filter */}
                            <FormControl fullWidth size="small">
                                <InputLabel>Month</InputLabel>
                                <Select
                                    value={filters.month}
                                    label="Month"
                                    onChange={(e) => {
                                        handleFilterChange('month', e.target.value);
                                        if (!e.target.value) {
                                            handleFilterChange('year', '');
                                        }
                                        if (e.target.value) {
                                            handleFilterChange('start_date', '');
                                            handleFilterChange('end_date', '');
                                        }
                                    }}
                                >
                                    <MenuItem value="">All Months</MenuItem>
                                    {months.map((month) => (
                                        <MenuItem key={month.value} value={month.value}>
                                            {month.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Year Filter */}
                            <FormControl fullWidth size="small">
                                <InputLabel>Year</InputLabel>
                                <Select
                                    value={filters.year}
                                    label="Year"
                                    onChange={(e) => {
                                        handleFilterChange('year', e.target.value);
                                        if (e.target.value) {
                                            handleFilterChange('start_date', '');
                                            handleFilterChange('end_date', '');
                                        }
                                    }}
                                    disabled={!filters.month}
                                >
                                    <MenuItem value="">All Years</MenuItem>
                                    {years.map((year) => (
                                        <MenuItem key={year} value={year.toString()}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Balance Min Filter */}
                            <TextField
                                label="Min Balance"
                                type="number"
                                size="small"
                                value={filters.balance_min}
                                onChange={(e) => handleFilterChange('balance_min', e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                }}
                                fullWidth
                            />

                            {/* Balance Max Filter */}
                            <TextField
                                label="Max Balance"
                                type="number"
                                size="small"
                                value={filters.balance_max}
                                onChange={(e) => handleFilterChange('balance_max', e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                }}
                                fullWidth
                            />
                        </Box>
                    </Collapse>
                </Box>

                {/* Clear Filters Button */}
                {hasActiveFilters && (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            startIcon={<Clear />}
                            onClick={handleClearFilters}
                            variant="outlined"
                            size="small"
                            color="secondary"
                        >
                            Clear All Filters
                        </Button>
                    </Box>
                )}
            </Paper>

            <Paper sx={{ width: '100%', overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortField === 'date'}
                                        direction={sortField === 'date' ? sortDirection : 'asc'}
                                        onClick={() => handleSort('date')}
                                    >
                                        Date
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortField === 'account_name'}
                                        direction={sortField === 'account_name' ? sortDirection : 'asc'}
                                        onClick={() => handleSort('account_name')}
                                    >
                                        Account
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortField === 'account_type'}
                                        direction={sortField === 'account_type' ? sortDirection : 'asc'}
                                        onClick={() => handleSort('account_type')}
                                    >
                                        Type
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">
                                    <TableSortLabel
                                        active={sortField === 'balance'}
                                        direction={sortField === 'balance' ? sortDirection : 'asc'}
                                        onClick={() => handleSort('balance')}
                                    >
                                        Balance
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortField === 'description'}
                                        direction={sortField === 'description' ? sortDirection : 'asc'}
                                        onClick={() => handleSort('description')}
                                    >
                                        Description
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : snapshots.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                                        <Typography color="text.secondary">
                                            No balances in this period. Month-end figures appear
                                            automatically from your transactions; you can also add one
                                            by hand for an account nothing syncs.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                snapshots.map((row) => (
                                    <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                                        <TableCell>{new Date(row.date).toLocaleDateString()}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500}>
                                                {row.account_name || 'Unknown Account'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={row.account_type}
                                                size="small"
                                                color={row.account_type === 'Asset' ? 'success' : row.account_type === 'Debt' ? 'error' : 'primary'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                                            {formatCurrency(row.balance, row.account_currency || 'USD')}
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {row.description}
                                        </TableCell>
                                        <TableCell align="right">
                                            {/* A derived row is arithmetic over the ledger, not data
                                                someone entered. Offering Edit would invite correcting
                                                a sum, and Delete would remove a row that reappears on
                                                the next load -- so both are withheld and the reason
                                                is stated rather than left to be discovered. */}
                                            {(row as any).derived ? (
                                                <Tooltip title="Calculated from your transactions. Edit the underlying entries to change it.">
                                                    <Chip label="from ledger" size="small" variant="outlined" sx={{ height: 20 }} />
                                                </Tooltip>
                                            ) : (
                                                <>
                                                    <IconButton size="small" onClick={() => handleEditClick(row)} color="primary">
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" onClick={() => handleDeleteClick(row)} color="error" sx={{ ml: 1 }}>
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            <Dialog open={editDialogOpen} onClose={closeEditDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Snapshot</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Date"
                            type="date"
                            value={editForm.date}
                            onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Balance"
                            type="number"
                            value={editForm.balance}
                            onChange={(e) => setEditForm({ ...editForm, balance: e.target.value })}
                            fullWidth
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                        />
                        <TextField
                            label="Description"
                            value={editForm.description}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            fullWidth
                            multiline
                            rows={3}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeEditDialog}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" disabled={saving}>
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={addDialogOpen} onClose={closeAddDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Add Snapshot</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel id="account-select-label">Account</InputLabel>
                            <Select
                                labelId="account-select-label"
                                label="Account"
                                value={addForm.account_id}
                                onChange={(e) => setAddForm({ ...addForm, account_id: e.target.value })}
                            >
                                {accounts.map((account) => (
                                    <MenuItem key={account.id} value={account.id}>
                                        {account.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Date"
                            type="date"
                            value={addForm.date}
                            onChange={(e) => setAddForm({ ...addForm, date: e.target.value })}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Balance"
                            type="number"
                            value={addForm.balance}
                            onChange={(e) => setAddForm({ ...addForm, balance: e.target.value })}
                            fullWidth
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                        />
                        <TextField
                            label="Description"
                            value={addForm.description}
                            onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                            fullWidth
                            multiline
                            rows={3}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeAddDialog}>Cancel</Button>
                    <Button onClick={handleAddSnapshot} variant="contained" disabled={adding}>
                        {adding ? 'Adding...' : 'Add Snapshot'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
                <DialogTitle>Delete Snapshot</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the snapshot for{' '}
                        <strong>{deletingSnapshot?.account_name}</strong> on{' '}
                        {deletingSnapshot ? new Date(deletingSnapshot.date).toLocaleDateString() : ''}?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </MainLayout>
    );
};
